<template>
  <div>
    <h1 class="page-title text-left">Larval Site Master</h1>
    <div class="panel">
      <div class="panel-heading">
        <h3 class="panel-title">
          Details
          <router-link
            :to="{ name: 'data-larval-sites' }"
            class="pull-right ml-10"
            ><i class="fa fa-list"></i
          ></router-link>
          <i
            class="fa fa-pencil pointer pull-right"
            v-on:click="form.showEdit({ id: id, list: true })"
          ></i>
        </h3>
      </div>
      <div class="panel-body">
        <dl class="dl-horizontal">
          <dt>Site Name or Id</dt>
          <dd>{{ siteName(form.data.siteId) }}</dd>

          <dt>Active</dt>
          <dd>
            <i
              class="fa"
              :class="{
                'fa-check-square-o': form.data.isActive,
                'fa-square-o': !form.data.isActive,
              }"
            ></i>
          </dd>

          <dt>Coordinates</dt>
          <dd
            v-if="hasCoordinates"
            class="pointer"
            v-on:click="
              showMap({
                lat: form.data.coordinates[0].lat,
                lon: form.data.coordinates[0].lon,
              })
            "
          >
            <i class="fa fa-map"></i> {{ form.data.coordinates[0].lat }},
            {{ form.data.coordinates[0].lon }}
          </dd>
          <dd v-else></dd>

          <dt>Address or Physical Location</dt>
          <dd
            class="pointer"
            v-on:click="
              showMap(
                `${form.data.address}, ${form.data.city} ${stateName(
                  form.data.stateId
                )} ${form.data.zip} `
              )
            "
          >
            <i class="fa fa-map"></i>
            {{ form.data.address }}<br />
            {{ form.data.city }}, {{ stateName(form.data.stateId) }}
            {{ form.data.zip }}
          </dd>

          <dt>Zone</dt>
          <dd>{{ zoneName(form.data.zoneId) }}</dd>

          <dt>Municipality</dt>
          <dd>{{ municipalityName(form.data.municipalityId) }}</dd>

          <dt>County</dt>
          <dd>{{ countyName(form.data.countyId) }}</dd>

          <dt>Route</dt>
          <dd>{{ routeName(form.data.routeId) }}</dd>

          <dt>Route Order</dt>
          <dd>{{ form.data.routeOrder }}</dd>

          <dt>Found By</dt>
          <dd>{{ form.data.foundBy }}</dd>

          <dt>Found Date</dt>
          <dd>{{ form.data.foundDate | date }}</dd>

          <dt>Site Acreage</dt>
          <dd>{{ form.data.acreage }}</dd>

          <dt>Permission</dt>
          <dd>{{ form.data.permission }}</dd>

          <dt>Landowner</dt>
          <dd>{{ form.data.landowner }}</dd>

          <dt>Landowner Address</dt>
          <dd
            class="pointer"
            v-on:click="
              showMap(
                `${form.data.landownerAddress}, ${
                  form.data.landownerCity
                } ${stateName(form.data.landownerStateId)} ${
                  form.data.landownerZip
                } `
              )
            "
          >
            <i class="fa fa-map"></i>
            {{ form.data.landownerAddress }}<br />
            {{ form.data.landownerCity }},
            {{ stateName(form.data.landownerStateId) }}
            {{ form.data.landownerZip }}
          </dd>

          <dt>Contact</dt>
          <dd>
            <span v-if="form.data.contactDate">{{
              form.data.contactDate | date
            }}</span
            ><br />
            {{ form.data.contactPerson }}<br />
            {{ form.data.phoneNumber }}
          </dd>

          <dt>Unincorporated</dt>
          <dd>{{ form.data.unincorporated }}</dd>

          <dt>Habitat 1</dt>
          <dd>{{ habitatName(form.data.habitatId) }}</dd>

          <dt>Habitat 2</dt>
          <dd>{{ habitatName(form.data.habitat2Id) }}</dd>

          <dt>Water 1</dt>
          <dd>{{ waterName(form.data.waterId) }}</dd>

          <dt>Water 2</dt>
          <dd>{{ waterName(form.data.water2Id) }}</dd>

          <dt>Schedule</dt>
          <dd>
            <span v-if="form.data.scheduleFrequency">
              {{ form.data.scheduleStart | date }}<br />
              every {{ form.data.scheduleFrequency }} days
            </span>
          </dd>

          <dt>Nuisance Program</dt>
          <dd>
            <i
              class="fa"
              :class="{
                'fa-check-square-o': form.data.nuisanceProgram,
                'fa-square-o': !form.data.nuisanceProgram,
              }"
            ></i>
          </dd>

          <dt>Site is Mandatory</dt>
          <dd>
            <i
              class="fa"
              :class="{
                'fa-check-square-o': form.data.isMandatory,
                'fa-square-o': !form.data.isMandatory,
              }"
            ></i>
          </dd>
        </dl>

        <div>
          <!-- Nav tabs -->
          <ul class="nav nav-tabs" role="tablist">
            <li role="presentation" class="active">
              <a href="#larvicide" role="tab" data-toggle="tab">Larvicide</a>
            </li>
            <li role="presentation">
              <a href="#comments" role="tab" data-toggle="tab">Comments</a>
            </li>
          </ul>

          <!-- Tab panes -->
          <div class="tab-content">
            <div role="tabpanel" class="tab-pane active" id="larvicide">
              <router-link
                :to="{
                  name: 'data-larvicide-with',
                  params: { type: 'ls', id: id },
                }"
                class="btn btn-primary pull-right"
                >Add Larvicide</router-link
              >

              <div class="col-xs-12 table-responsive">
                <table class="table table-striped">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Applicator</th>
                      <th>Temperature</th>
                      <th>Wind Speed</th>
                      <th>Wind Direction</th>
                      <th>Equipment Used</th>
                      <th>Eggs</th>
                      <th>Larvae</th>
                      <th>Pupae</th>
                      <th>Adults</th>
                      <th>Visual Monitoring</th>
                      <th>Control Action</th>
                      <th>Chemical</th>
                      <th>Amount (oz)</th>
                      <th>Appl. Rate</th>
                      <th>EPA Reg. No.</th>
                      <th>Area Treated (sq. ft.)</th>
                      <th>Sample Number</th>
                      <th>Wed/Dry</th>
                      <th>Service Request</th>
                      <th>Work Status</th>
                      <th>Predators</th>
                      <th>Biting Activity</th>
                      <th>Site Conditions</th>
                      <th>Larval Stages</th>
                      <th>% Breeding</th>
                      <th>% Culex</th>
                      <th>% Non Culex</th>
                      <th width="120"></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="larvicide in form.data.larvicides">
                      <td>{{ larvicide.appliedDate | date }}</td>
                      <td>{{ larvicide.appliedTime }}</td>
                      <td>{{ applicatorName(larvicide.applicatorId) }}</td>
                      <td>{{ larvicide.temperature }}</td>
                      <td>{{ larvicide.windSpeed }}</td>
                      <td>{{ windDirection(larvicide.windDirectionId) }}</td>
                      <td>{{ equipmentName(larvicide.lequipmentId) }}</td>
                      <td>{{ larvaCountName(larvicide.eggs) }}</td>
                      <td>{{ larvaCountName(larvicide.larvae) }}</td>
                      <td>{{ larvaCountName(larvicide.pupae) }}</td>
                      <td>{{ larvaCountName(larvicide.adults) }}</td>
                      <td>
                        <i
                          class="fa"
                          :class="{
                            'fa-check-square-o':
                              larvicide.vizualMonitorPerformed,
                            'fa-square-o': !larvicide.vizualMonitorPerformed,
                          }"
                        ></i>
                      </td>
                      <td>{{ controlAction(larvicide.controlActionId) }}</td>
                      <td>{{ chemicalName(larvicide.lchemicalId) }}</td>
                      <td>{{ larvicide.amount }}</td>
                      <td>{{ larvicide.applicationRate }}</td>
                      <td>{{ larvicide.epaRegNumber }}</td>
                      <td>{{ larvicide.areaTreated }}</td>
                      <td>{{ larvicide.sampleNumber }}</td>
                      <td>{{ larvicide.wetDry }}</td>
                      <td>
                        <router-link
                          v-if="form.data.serviceRequestId"
                          :to="{
                            name: 'data-service-request-details',
                            params: { id: form.data.serviceRequestId },
                          }"
                          >view</router-link
                        >
                      </td>
                      <td>{{ larvicide.workStatus }}</td>
                      <td>
                        <i
                          class="fa"
                          :class="{
                            'fa-check-square-o': larvicide.predators,
                            'fa-square-o': !larvicide.predators,
                          }"
                        ></i>
                      </td>
                      <td>
                        <i
                          class="fa"
                          :class="{
                            'fa-check-square-o': larvicide.bitingActivity,
                            'fa-square-o': !larvicide.bitingActivity,
                          }"
                        ></i>
                      </td>
                      <td>{{ larvicide.siteConditions }}</td>
                      <td>{{ larvicide.lStages }}</td>
                      <td>{{ larvicide.percentBreeding }}</td>
                      <td>{{ larvicide.percentCulex }}</td>
                      <td>{{ larvicide.percentNonCulex }}</td>

                      <td>
                        <router-link
                          :to="{
                            name: 'data-larvicide-details',
                            params: { id: larvicide.id },
                          }"
                          ><i
                            class="fa fa-ellipsis-h"
                            style="margin-right: 10px;"
                          ></i
                        ></router-link>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div role="tabpanel" class="tab-pane" id="comments">
              <table class="table table-striped">
                <thead>
                  <th>Date</th>
                  <th>By</th>
                  <th>Comment</th>
                </thead>
                <tbody>
                  <tr v-for="comment in form.data.comments">
                    <td>{{ comment.updatedAt | date }}</td>
                    <td>
                      {{ comment.user.firstName }} {{ comment.user.lastName }}
                    </td>
                    <td>{{ comment.body }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>

    <site-form :form="form"></site-form>

    <google-map ref="gmap"></google-map>
  </div>
</template>

<script>
import LarvalSitesService from '@/services/LarvalSitesService';
import Form from '@/utils/form';
import loader from '@/utils/loader';
import siteForm from '@/components/forms/larval_site';
import googleMap from '@/components/googleMap';
import _ from 'lodash';
import store from '@/store';

export default {
  name: 'larval_site_details',

  components: {
    siteForm,
    googleMap,
  },

  props: ['id'],

  data() {
    return {
      form: new Form(LarvalSitesService, {
        coordinates: [{ lat: null, lon: null }],
      }),
    };
  },

  computed: {
    hasCoordinates() {
      return _.has(this.form, 'data.coordinates[0].lat');
    },
  },

  methods: {
    siteName(id) {
      return this.$store.getters.siteNameById(id);
    },
    routeName(id) {
      return this.$store.getters.routeNameById(id);
    },
    countyName(id) {
      return this.$store.getters.countyNameById(id);
    },
    municipalityName(id) {
      return this.$store.getters.municipalityNameById(id);
    },
    habitatName(id) {
      return this.$store.getters.habitatNameById(id);
    },
    waterName(id) {
      return this.$store.getters.waterNameById(id);
    },
    stateName(id) {
      return this.$store.getters.stateNameById(id);
    },
    zoneName(id) {
      return this.$store.getters.zoneNameById(id);
    },
    contractName(id) {
      return this.$store.getters.contractNameById(id);
    },
    applicatorName(id) {
      return this.$store.getters.applicatorNameById(id);
    },
    windDirection(id) {
      return this.$store.getters.windDirectionNameById(id);
    },
    equipmentName(id) {
      return this.$store.getters.lEquipementNameById(id);
    },
    vizualMonitor(id) {
      return this.$store.getters.vizualMonitorNameById(id);
    },
    controlAction(id) {
      return this.$store.getters.controlActionNameById(id);
    },
    chemicalName(id) {
      return this.$store.getters.lChemicalNameById(id);
    },
    larvaCountName(id) {
      return this.$store.getters.larvaCountNameById(id);
    },
    showMap(coordinates) {
      this.$refs.gmap.showMap(coordinates);
    },
  },

  mounted() {
    loader.lookups().then(() => {
      this.form.getDetails(this.id);
    });
  },
};
</script>

<style lang="scss">
.nav-tabs {
  margin-top: 50px;
}
</style>
